/**
 * Globe Background Component
 * 
 * Displays a rotating globe map in the background using Mapbox GL JS.
 * The globe rotates continuously and matches the selected language.
 */

import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import type { MapLanguage } from '../types';
import { getApiConfig } from '../config/api';
import styles from './GlobeBackground.module.css';

/**
 * Props for Globe Background Component
 */
export interface GlobeBackgroundProps {
  /** Map language/locale */
  language: MapLanguage;
}

/**
 * Globe Background Component
 * 
 * Renders a rotating globe map in the background.
 */
export const GlobeBackground: React.FC<GlobeBackgroundProps> = ({ language }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<mapboxgl.Map | null>(null);
  const languagePlugin = useRef<MapboxLanguage | null>(null);
  const spinTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    // Initialize map only once
    if (!mapContainer.current || mapInstance.current) {
      return;
    }

    try {
      const config = getApiConfig();
      mapboxgl.accessToken = config.mapboxToken;

      // Initialize map with globe projection
      const map = new mapboxgl.Map({
        container: mapContainer.current!,
        style: 'mapbox://styles/mapbox/streets-v12',
        projection: 'globe' as any, // Globe projection
        center: [0, 0], // Start at 0 longitude
        zoom: 1.5,
        pitch: 0, // Keep pitch at 0 for globe view
        bearing: 0, // Start at 0 bearing
        interactive: false, // Disable user interaction
        attributionControl: false, // Hide attribution
      });

      mapInstance.current = map;

      // Initialize language plugin
      const langPlugin = new MapboxLanguage({
        defaultLanguage: language,
      });
      map.addControl(langPlugin);
      languagePlugin.current = langPlugin;

      // Wait for map to load, then start rotation
      map.on('load', () => {
        // Rotation speed: following Mapbox globe-spin example
        // 360 degrees per 120 seconds = 3 degrees/second
        const secondsPerRevolution = 120;
        
        // Get degrees per second
        let distancePerSecond = 360 / secondsPerRevolution;

        // Function to spin the globe
        const spinGlobe = () => {
          const center = map.getCenter();
          // Decrease longitude for west-to-east rotation (Earth's natural rotation)
          center.lng -= distancePerSecond;
          
          // Smoothly animate the map over one second
          // When this animation is complete, it calls a 'moveend' event
          map.easeTo({
            center,
            duration: 1000,
            easing: (n) => n, // Linear easing
          });
        };

        // When animation is complete, start spinning again
        const handleMoveEnd = () => {
          if (mapInstance.current) {
            spinTimeoutRef.current = window.setTimeout(() => {
              spinGlobe();
            }, 0);
          }
        };

        map.on('moveend', handleMoveEnd);
        
        // Start the rotation
        spinGlobe();
      });

      // Cleanup on unmount
      return () => {
        if (spinTimeoutRef.current !== null) {
          clearTimeout(spinTimeoutRef.current);
        }
        if (mapInstance.current) {
          mapInstance.current.remove();
          mapInstance.current = null;
        }
      };
    } catch (error) {
      console.error('Failed to initialize globe background:', error);
    }
  }, []); // Only run once on mount

  // Update language when it changes
  useEffect(() => {
    if (!mapInstance.current || !languagePlugin.current) {
      return;
    }

    const updateLanguage = () => {
      if (!mapInstance.current || !languagePlugin.current) {
        return;
      }

      try {
        // Check if style is loaded
        if (!mapInstance.current.isStyleLoaded()) {
          // Wait for style to load
          mapInstance.current.once('style.load', updateLanguage);
          return;
        }

        // Update language using the plugin
        const currentStyle = mapInstance.current.getStyle();
        if (currentStyle) {
          const modifiedStyle = languagePlugin.current.setLanguage(currentStyle, language);
          mapInstance.current.setStyle(modifiedStyle);
        }
      } catch (error) {
        console.error('Failed to update language:', error);
        // If style is not loaded, wait for it
        if (mapInstance.current) {
          mapInstance.current.once('style.load', updateLanguage);
        }
      }
    };

    updateLanguage();
  }, [language]);

  return <div ref={mapContainer} className={styles.globeContainer} />;
};

